import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import SpotifyEmbed from '../SpotifyEmbed';

const API = process.env.REACT_APP_API_URL || '';

function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function RecordingDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState({}); // isrc -> true, for collapsing one back down

  const toggleListen = (isrc) => setHidden(h => ({ ...h, [isrc]: !h[isrc] }));

  useEffect(() => {
    setData(null);
    setError('');
    setHidden({});
    axios.get(`${API}/api/library/recordings/${id}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message));
  }, [id]);

  if (error) return (
    <PanelWrap>
      <PanelHeader><CloseBtn onClick={onBack}>✕</CloseBtn></PanelHeader>
      <ErrorBox>{error}</ErrorBox>
    </PanelWrap>
  );

  if (!data) return (
    <PanelWrap>
      <PanelHeader><CloseBtn onClick={onBack}>✕</CloseBtn></PanelHeader>
      <EmptyState>Loading...</EmptyState>
    </PanelWrap>
  );

  return (
    <PanelWrap>
      <PanelHeader><CloseBtn onClick={onBack}>✕</CloseBtn></PanelHeader>

      <TitleBlock>
        <h1>{data.title || 'Untitled'}</h1>
        {data.version && <VersionTag>{data.version}</VersionTag>}
      </TitleBlock>
      <Artist>{data.primary_artist || 'Unknown artist'}</Artist>

      <StatRow>
        <Stat><StatLabel>Duration</StatLabel><StatValue>{formatDuration(data.duration_seconds)}</StatValue></Stat>
        <Stat><StatLabel>Canonical ISRC</StatLabel><StatValue mono>{data.canonical_isrc || '—'}</StatValue></Stat>
        <Stat><StatLabel>Fingerprint</StatLabel><StatValue>{data.fingerprint ? 'On file' : 'Not yet captured'}</StatValue></Stat>
        <Stat><StatLabel>Source</StatLabel><StatValue>{data.source === 'orchard_import' ? 'Orchard import' : 'Upload confirmed'}</StatValue></Stat>
        <Stat>
          <StatLabel>Language{data.languages?.length === 1 ? '' : 's'}</StatLabel>
          <StatValue>{data.languages?.length > 0 ? data.languages.join(', ') : 'Unknown'}</StatValue>
        </Stat>
      </StatRow>

      {data.duplicateCandidates.length > 0 && (
        <Section>
          <SectionTitle warn>⚠ Possible duplicates pending review</SectionTitle>
          <Muted>Fingerprint match found with {data.duplicateCandidates.length} other recording(s). Review in the duplicate queue to merge.</Muted>
        </Section>
      )}

      <Section>
        <SectionTitle>ISRCs on file ({data.isrcs.length})</SectionTitle>
        {data.isrcs.length > 1 && (
          <Muted style={{ marginBottom: 10, display: 'block' }}>
            This recording has been issued under multiple ISRCs historically. Listen to each to compare —
            the canonical one is used for new exports.
          </Muted>
        )}
        <IsrcRows>
          {data.isrcs.map(i => (
            <IsrcRow key={i.isrc}>
              <IsrcRowTop>
                <IsrcChip canonical={i.is_canonical}>
                  <Mono>{i.isrc}</Mono>
                  {i.is_canonical ? <CanonicalTag>canonical</CanonicalTag> : null}
                </IsrcChip>
                <ListenBtn onClick={() => toggleListen(i.isrc)}>
                  {hidden[i.isrc] ? 'Show' : 'Hide'}
                </ListenBtn>
              </IsrcRowTop>
              {!hidden[i.isrc] && (
                <EmbedSlot><SpotifyEmbed isrc={i.isrc} compact /></EmbedSlot>
              )}
            </IsrcRow>
          ))}
        </IsrcRows>
      </Section>

      <Section>
        <SectionTitle>Appears on {data.releases.length} release{data.releases.length === 1 ? '' : 's'}</SectionTitle>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>Release</th>
                <th>Product Code</th>
                <th>Release Date</th>
                <th>Configuration</th>
                <th>ISRC used</th>
                <th>Track #</th>
              </tr>
            </thead>
            <tbody>
              {data.releases.map(r => (
                <tr key={r.id + '_' + r.track_position}>
                  <td>{r.release_name || '—'}</td>
                  <td><Mono>{r.product_code || '—'}</Mono></td>
                  <td><Mono>{r.release_date || '—'}</Mono></td>
                  <td>{r.configuration || '—'}</td>
                  <td><Mono>{r.isrc_used || '—'}</Mono></td>
                  <td>{r.track_position || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </Section>
    </PanelWrap>
  );
}

const PanelWrap = styled.div`
  padding: 20px 24px 40px;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const CloseBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-3);
  color: var(--text-2);
  font-size: 14px;
  font-weight: 700;
  transition: all 0.15s;
  &:hover {
    background: var(--border);
    color: var(--text);
  }
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  h1 {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 26px;
    color: var(--text);
  }
`;

const VersionTag = styled.span`
  font-size: 13px;
  color: var(--text-2);
  font-weight: 600;
`;

const Artist = styled.div`
  color: var(--text-2);
  font-size: 15px;
  margin-top: 2px;
  margin-bottom: 20px;
`;

const StatRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const Stat = styled.div`
  background: var(--bg-2);
  border-radius: var(--radius);
  padding: 12px 16px;
  box-shadow: var(--shadow-sm);
  min-width: 140px;
`;

const StatLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-3);
  font-weight: 700;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: ${p => p.mono ? 400 : 700};
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'inherit'};
  color: var(--text);
`;

const Section = styled.div`
  margin-bottom: 26px;
`;

const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.warn ? 'var(--amber)' : 'var(--text)'};
  margin-bottom: 10px;
`;

const IsrcRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IsrcRow = styled.div`
  background: var(--bg-3);
  border-radius: var(--radius);
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IsrcRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const IsrcChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.canonical ? 'var(--accent-dim)' : 'var(--bg-2)'};
  border: 1px solid ${p => p.canonical ? 'var(--accent)' : 'var(--border)'};
  border-radius: var(--radius);
  padding: 5px 12px;
`;

const ListenBtn = styled.button`
  background: none;
  border: 1px solid var(--border);
  color: var(--text-2);
  font-size: 11px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: var(--radius);
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { border-color: var(--accent-border); color: var(--accent-text); }
`;

const EmbedSlot = styled.div`
  padding-top: 2px;
`;

const CanonicalTag = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--blue-dark);
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-2);
`;

const Muted = styled.span`
  color: var(--text-3);
  font-size: 12px;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  background: var(--bg-2);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);

  thead th {
    text-align: left;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-3);
    font-weight: 700;
    padding: 10px 14px;
    border-bottom: 1.5px solid var(--border);
    background: var(--bg-3);
  }

  tbody td {
    padding: 10px 14px;
    font-size: 13px;
    border-bottom: 1px solid var(--border);
  }

  tbody tr:last-child td { border-bottom: none; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 14px;
  font-size: 13px;
`;

export default RecordingDetail;
